"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getEnumKeysAndValues = exports.getEnumKeys = exports.getNumericEnumStringKeys = exports.isNumericEnum = exports.isStringEnum = exports.getMixedEnumKeys = void 0;
/**
 * Filters out numeric keys that are part of TypeScript's two-way mapping for numeric enums
 */
const getMixedEnumKeys = (enumObject) => {
    const keys = Object.keys(enumObject);
    const values = Object.values(enumObject);
    return keys.filter((key) => {
        const value = enumObject[key];
        return typeof value === "number" || !values.includes(Number(key));
    });
};
exports.getMixedEnumKeys = getMixedEnumKeys;
const isStringEnum = (enumObject) => {
    const values = Object.values(enumObject);
    return values.every((value) => typeof value === "string");
};
exports.isStringEnum = isStringEnum;
/**
 * Checks if half of the values are number (as numeric enums are two-way mapped)
 */
const isNumericEnum = (enumObject) => {
    const values = Object.values(enumObject);
    const numberValues = values.filter((value) => typeof value === "number");
    return numberValues.length === values.length / 2;
};
exports.isNumericEnum = isNumericEnum;
/**
 * Returns the string keys of a numeric enum.
 *
 * @param enum - numeric enum to get keys from
 * @returns array of keys
 *
 * @remarks
 * This is useful for when you want to iterate over the keys of a numeric enum.
 * As numeric enums are two-way mapped, you can't just use `Object.keys` to get only the keys.
 * It will return both the keys and values, so you need to filter out the values.
 * This function does that for you.
 *
 * @example
 * enum NumberColors {
 *  Red,
 *  Yellow,
 *  Blue
 * }
 *
 * getNumericEnumKeys(NumberColors) // ['Red', 'Yellow', 'Blue']
 *
 * @example
 * enum StringColors {
 *    Red = "Red",
 *    Yellow = "Yellow",
 *    Blue = "Blue",
 * }
 *
 * getNumericEnumStringKeys(StringColors); // TS error, only numeric enums are supported
 *
 * @example
 * enum MixedColors {
 *    Red,
 *    Yellow = "Yellow",
 *    Blue = 1,
 * }
 *
 * getNumericEnumStringKeys(MixedColors); // TS error, only numeric enums are supported
 */
const getNumericEnumStringKeys = (numericEnum) => {
    return Object.values(numericEnum).filter((value) => typeof value === "string");
};
exports.getNumericEnumStringKeys = getNumericEnumStringKeys;
/**
 * Gets keys of enum independently of whether it is a numeric, heterogeneous or string enum.
 *
 * @param enumObject - enum to get keys from
 * @returns array of string keys
 *
 * @example
 * enum NumberColors {
 *  Red,
 *  Yellow,
 *  Blue
 * }
 *
 * getEnumKeys(NumberColors) // ['Red', 'Yellow', 'Blue']
 *
 * @example
 * enum StringColors {
 *  Red = "Red",
 *  Yellow = "Yellow",
 *  Blue = "Blue",
 * }
 *
 * getEnumKeys(StringColors); // ['Red', 'Yellow', 'Blue']
 *
 * @example
 * enum MixedColors {
 *  Red,
 *  Yellow = "Yellow",
 *  Blue = 1,
 * }
 *
 * getEnumKeys(MixedColors); // ['Red', 'Yellow', 'Blue']
 *
 * @example
 * enum NumericColorsDefined {
 *  Red = 1,
 *  Yellow = 2,
 *  Blue = 3,
 * }
 *
 * getEnumKeys(NumericColorsDefined); // ['Red', 'Yellow', 'Blue']
 */
const getEnumKeys = (enumObject) => {
    if ((0, exports.isStringEnum)(enumObject)) {
        return Object.keys(enumObject);
    }
    if ((0, exports.isNumericEnum)(enumObject)) {
        return (0, exports.getNumericEnumStringKeys)(enumObject);
    }
    return (0, exports.getMixedEnumKeys)(enumObject);
};
exports.getEnumKeys = getEnumKeys;
const getEnumKeysAndValues = (enumObject) => {
    const enumKeys = (0, exports.getEnumKeys)(enumObject);
    return enumKeys.map((key) => {
        return {
            key,
            value: enumObject[key],
        };
    });
};
exports.getEnumKeysAndValues = getEnumKeysAndValues;
//# sourceMappingURL=getEnumKeys.js.map